/**
 * Canvas Decision Logger + Debug Session Recorder
 *
 * Activate: add `?canvasDebug=true` to the Workbench URL (light mode).
 *           Or `?canvasDebug=deep` to include the per-node tx store and the
 *           latest investigation section results — required by the audit
 *           script in india-le-platform/scripts/audit_canvas_snapshot.mjs.
 * Output:   browser console with structured JSON for each decision.
 * Export:   Press Ctrl+Shift+E to download session as JSON, or call exportSession().
 *
 * The session recorder captures every pipeline event with timestamps so we can
 * replay what happened: what the user did → what APIs returned → what reached
 * the canvas → what the Node Inspector showed.
 *
 * Combine with Railway backend logs (same timestamps) for full pipeline audit.
 */

// ── Session recorder ────────────────────────────────────────────
export type DebugEntry = {
  t: string;         // ISO timestamp
  ms: number;        // ms since session start (for relative timing)
  tag: string;       // CANVAS | INSPECTOR | TXSTORE | TRIAGE | ENTITY | TRACE | RESET | EDGE | NODE | etc.
  event: string;     // human-readable event name
  data: any;         // structured payload (kept small — addresses truncated)
};

const _sessionLog: DebugEntry[] = [];
let _sessionStart = 0;

function _record(tag: string, event: string, data: any): void {
  if (!isCanvasDebugEnabled()) return;
  if (_sessionStart === 0) _sessionStart = Date.now();
  _sessionLog.push({
    t: new Date().toISOString(),
    ms: Date.now() - _sessionStart,
    tag,
    event,
    data,
  });
  // Cap at 5000 entries to avoid memory issues
  if (_sessionLog.length > 5000) _sessionLog.splice(0, 500);
}

/**
 * JSON.stringify replacer that drops circular references with a
 * "[Circular]" marker. Cytoscape elements (especially cluster nodes
 * with `bundled_nodes`) reference their neighbours, which Cytoscape
 * extends with back-pointers during rendering — that's enough to make
 * the whole structure self-referential. We don't need fidelity for
 * those; we just need the export to not throw.
 *
 * Also bails out on values that look like a Cytoscape internal handle
 * (`_private`, `cy()` method, scratch object) so we don't accidentally
 * serialise a 1-MB internal state blob.
 */
function _safeJsonReplacer() {
  const seen = new WeakSet();
  return (_key: string, value: any): any => {
    if (value === null || typeof value !== 'object') return value;
    // Cytoscape element-like? Drop, replace with a tiny shadow.
    if (typeof value._private === 'object' || typeof value.scratch === 'function') {
      return { _cy_element: true, id: value.id?.() ?? value._private?.data?.id };
    }
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    return value;
  };
}

// ── Canvas-state introspection (presentation layer) ──────────────────────
//
// The DebugEntry log above captures DATA-LAYER decisions (which edges were
// minted, what data props they carry). It does NOT capture what Cytoscape
// actually rendered — computed line-color, width, opacity, overlay-color,
// node positions, sizes, label text. Those live behind cy.style() lookups
// at render time.
//
// `inspectCanvasStyles()` walks the live `cy` instance and dumps the
// presentation layer in a serialisable shape. Two consumers:
//   1. Playwright specs that need to assert §10.3-§10.7 palette compliance
//      end-to-end on a real graph (not synthetic single-edge fixtures).
//   2. The Ctrl+Shift+E download — combined with the DebugEntry log this
//      gives one JSON that captures BOTH the data decisions AND the visual
//      output, so a session can be replayed AND palette-audited offline.
//
// Channels captured map directly to §10:
//   §10.3 stroke colour          → line-color, target-arrow-color, source-arrow-color
//   §10.4 trace-path overlay     → overlay-color, overlay-opacity, overlay-padding
//   §10.5 magnitude width        → width
//   §10.6 certainty              → line-style
//   §10.7 focus state            → opacity, _visibility (data prop)
//   §10.10 label format          → label, font-size
// Plus position/size for ELK layout sanity checks.

type EdgeStyleDump = {
  type: 'edge';
  id: string;
  source: string;
  target: string;
  data: {
    edgeType?: string;
    forensic_edge?: string;
    forensic_record_id?: string | number;
    onTracePath?: boolean;
    canvas_intent?: string;
    importance?: string;
    is_bundle?: boolean;
    utxo_secondary?: boolean;
    privacy_sanctioned?: boolean;
    _widthTier?: 'thin' | 'medium' | 'thick';
    _visibility?: number;
    txCount?: number;
    value?: number;
    token_symbol?: string;
    label?: string;
  };
  computed: {
    'line-color': string;
    'line-style': string;
    'target-arrow-color': string;
    'source-arrow-color': string;
    'target-arrow-shape': string;
    'width': number;
    'opacity': number;
    'overlay-color': string;
    'overlay-opacity': number;
    'overlay-padding': number;
    'curve-style': string;
    'z-index': number;
    'label': string;
    'font-size': number;
    'color': string;
  };
  classes: string[];
};

type NodeStyleDump = {
  type: 'node';
  id: string;
  data: {
    entity_name?: string;
    entity_type?: string;
    entity_category?: string;
    chain?: string;
    type?: string;
    forensic?: string;
    is_contract?: boolean;
    is_privacy_chain?: boolean;
    is_root?: boolean;
    canvas_intent?: string;
    overlayColor?: string;
    _visibility?: number;
  };
  position: { x: number; y: number };
  size: { width: number; height: number };
  bounding_box: { x1: number; y1: number; x2: number; y2: number };
  computed: {
    'background-color': string;
    'border-color': string;
    'border-width': number;
    'border-style': string;
    'shape': string;
    'opacity': number;
    'label': string;
    'color': string;
    'font-size': number;
    'overlay-color': string;
    'overlay-opacity': number;
  };
  classes: string[];
};

type CanvasStateDump = {
  available: boolean;
  reason?: string;
  zoom?: number;
  pan?: { x: number; y: number };
  viewport?: { width: number; height: number };
  counts?: { nodes: number; edges: number; hidden: number };
  // §10.3 audit summary — counts of edges per stroke-colour bucket so a
  // failing palette test can be diagnosed without walking the full elements
  // list. Buckets are keyed by the canonical hex; "other" surfaces any
  // colour outside the documented palette (the bug we're guarding against).
  // The `gold` bucket is intentional for trace overlays (gold-path,
  // primary-trace, danger-trace) — it would otherwise inflate `other`.
  palette_audit?: {
    grey: number;       // #94a3b8 / #cbd5e1
    blue: number;       // #3b82f6
    pink: number;       // #ec4899 / #db2777
    violet: number;     // #8b5cf6 / #7c3aed
    gold: number;       // #f59e0b / #fbbf24 (trace overlay — gold-path / primary-trace; bridge-hop dashed)
    purple: number;     // #a855f7 (mixer / CoinJoin / privacy edges, dashed — tender sheet #10)
    other: number;      // anything else — should be 0 post-§10.3
    other_samples: Array<{ id: string; color: string; edgeType?: string }>;
  };
  // §10.5 width tier audit — counts per tier so the histogram is visible at
  // a glance. Canonical widths are {1.0, 1.5, 2.0, 3.5} per GraphCanvas.tsx
  // style rules: 1.0 = standard, 1.5 = secondary/emphasis/dex-swap/inferred,
  // 2.0 = trace primary, 3.5 = thick / heavy-volume. Anything else → `other`.
  width_audit?: {
    thin: number;       // 1.0
    light: number;      // 1.5
    medium: number;     // 2.0
    thick: number;      // 3.5
    other: number;
    other_samples: Array<{ id: string; width: number; edgeType?: string }>;
  };
  nodes?: NodeStyleDump[];
  edges?: EdgeStyleDump[];
};

const PALETTE_GREY = new Set(['#94a3b8', '#cbd5e1', 'rgb(148,163,184)', 'rgb(203,213,225)']);
const PALETTE_BLUE = new Set(['#3b82f6', 'rgb(59,130,246)']);
const PALETTE_PINK = new Set(['#ec4899', '#db2777', 'rgb(236,72,153)', 'rgb(219,39,119)']);
const PALETTE_VIOLET = new Set(['#8b5cf6', '#7c3aed', 'rgb(139,92,246)', 'rgb(124,58,237)']);
// Trace overlays use Tailwind amber-500 / amber-400. `gold-path`,
// `primary-trace`, and `danger-trace` all land here. Treating them as
// `other` made every healthy investigation look broken.
const PALETTE_GOLD = new Set(['#f59e0b', '#fbbf24', 'rgb(245,158,11)', 'rgb(251,191,36)']);
// Mixer / CoinJoin / privacy edges (dashed purple, distinct from DEX violet).
const PALETTE_PURPLE = new Set(['#a855f7', 'rgb(168,85,247)']);

function _normalizeColor(c: string): string {
  if (!c) return '';
  const s = c.trim().toLowerCase();
  // Cytoscape can return "rgb(148, 163, 184)" with spaces — squash for set lookup.
  return s.replace(/\s+/g, '');
}

function _classifyStroke(color: string): 'grey' | 'blue' | 'pink' | 'violet' | 'gold' | 'purple' | 'other' {
  const c = _normalizeColor(color);
  if (PALETTE_GREY.has(c)) return 'grey';
  if (PALETTE_BLUE.has(c)) return 'blue';
  if (PALETTE_PINK.has(c)) return 'pink';
  if (PALETTE_VIOLET.has(c)) return 'violet';
  if (PALETTE_GOLD.has(c)) return 'gold';
  if (PALETTE_PURPLE.has(c)) return 'purple';
  return 'other';
}

function _classifyWidth(w: number): 'thin' | 'light' | 'medium' | 'thick' | 'other' {
  // Cytoscape returns a number; tolerate small float drift. The 1.5 tier is
  // shared by SECONDARY_TRACE, edge-emphasized, dex-swap, inferred,
  // forensic_edge=undisclosed, etc. — see GraphCanvas.tsx for the full set.
  if (Math.abs(w - 1.0) < 0.05) return 'thin';
  if (Math.abs(w - 1.5) < 0.05) return 'light';
  if (Math.abs(w - 2.0) < 0.05) return 'medium';
  if (Math.abs(w - 3.5) < 0.05) return 'thick';
  return 'other';
}

/**
 * Walk the live cytoscape instance and return computed styles + positions
 * for every visible element. Returns `{available:false}` when the canvas
 * isn't initialised yet (called too early, no graph loaded, etc.).
 *
 * Safe to call at any time — never throws. Designed to be invoked from
 * Playwright via `page.evaluate(() => window.inspectCanvasStyles())`.
 */
export function inspectCanvasStyles(opts?: { includeElements?: boolean }): CanvasStateDump {
  const includeElements = opts?.includeElements !== false; // default on
  try {
    const cy = (window as any).__cy;
    if (!cy || typeof cy.elements !== 'function') {
      return { available: false, reason: 'cy_not_initialised' };
    }
    const allEdges = cy.edges();
    const allNodes = cy.nodes();
    const hiddenCount = cy.elements().filter((el: any) => {
      const v = el.data?.('_visibility');
      return v === 0;
    }).length;

    const paletteAudit = { grey: 0, blue: 0, pink: 0, violet: 0, gold: 0, purple: 0, other: 0, other_samples: [] as Array<{ id: string; color: string; edgeType?: string }> };
    const widthAudit = { thin: 0, light: 0, medium: 0, thick: 0, other: 0, other_samples: [] as Array<{ id: string; width: number; edgeType?: string }> };

    const edgeDumps: EdgeStyleDump[] = [];
    allEdges.forEach((e: any) => {
      const data = e.data() || {};
      const lineColor = e.style('line-color');
      const widthVal = parseFloat(e.style('width'));
      const bucket = _classifyStroke(lineColor);
      paletteAudit[bucket] = (paletteAudit[bucket] || 0) + 1;
      if (bucket === 'other' && paletteAudit.other_samples.length < 10) {
        paletteAudit.other_samples.push({ id: data.id, color: lineColor, edgeType: data.edgeType });
      }
      const wbucket = _classifyWidth(widthVal);
      widthAudit[wbucket] = (widthAudit[wbucket] || 0) + 1;
      if (wbucket === 'other' && widthAudit.other_samples.length < 10) {
        widthAudit.other_samples.push({ id: data.id, width: widthVal, edgeType: data.edgeType });
      }

      if (includeElements) {
        edgeDumps.push({
          type: 'edge',
          id: data.id,
          source: data.source,
          target: data.target,
          data: {
            edgeType: data.edgeType,
            forensic_edge: data.forensic_edge,
            forensic_record_id: data.forensic_record_id,
            onTracePath: data.onTracePath,
            canvas_intent: data.canvas_intent,
            importance: data.importance,
            is_bundle: data.is_bundle,
            utxo_secondary: data.utxo_secondary,
            privacy_sanctioned: data.privacy_sanctioned,
            _widthTier: data._widthTier,
            _visibility: data._visibility,
            txCount: data.txCount,
            value: data.value,
            token_symbol: data.token_symbol,
            label: data.label,
          },
          computed: {
            'line-color': lineColor,
            'line-style': e.style('line-style'),
            'target-arrow-color': e.style('target-arrow-color'),
            'source-arrow-color': e.style('source-arrow-color'),
            'target-arrow-shape': e.style('target-arrow-shape'),
            'width': widthVal,
            'opacity': parseFloat(e.style('opacity')),
            'overlay-color': e.style('overlay-color'),
            'overlay-opacity': parseFloat(e.style('overlay-opacity')) || 0,
            'overlay-padding': parseFloat(e.style('overlay-padding')) || 0,
            'curve-style': e.style('curve-style'),
            'z-index': parseInt(e.style('z-index')) || 0,
            'label': e.style('label'),
            'font-size': parseFloat(e.style('font-size')),
            'color': e.style('color'),
          },
          classes: typeof e.classes === 'function' ? e.classes() : [],
        });
      }
    });

    const nodeDumps: NodeStyleDump[] = [];
    if (includeElements) {
      allNodes.forEach((n: any) => {
        const data = n.data() || {};
        const pos = n.position?.() || { x: 0, y: 0 };
        const bb = n.boundingBox?.() || { x1: 0, y1: 0, x2: 0, y2: 0, w: 0, h: 0 };
        nodeDumps.push({
          type: 'node',
          id: data.id,
          data: {
            entity_name: data.entity_name,
            entity_type: data.entity_type,
            entity_category: data.entity_category,
            chain: data.chain,
            type: data.type,
            forensic: data.forensic,
            is_contract: data.is_contract,
            is_privacy_chain: data.is_privacy_chain,
            is_root: data.is_root,
            canvas_intent: data.canvas_intent,
            overlayColor: data.overlayColor,
            _visibility: data._visibility,
          },
          position: { x: Math.round(pos.x), y: Math.round(pos.y) },
          size: {
            width: Math.round(parseFloat(n.style('width')) || bb.w || 0),
            height: Math.round(parseFloat(n.style('height')) || bb.h || 0),
          },
          bounding_box: {
            x1: Math.round(bb.x1), y1: Math.round(bb.y1),
            x2: Math.round(bb.x2), y2: Math.round(bb.y2),
          },
          computed: {
            'background-color': n.style('background-color'),
            'border-color': n.style('border-color'),
            'border-width': parseFloat(n.style('border-width')) || 0,
            'border-style': n.style('border-style'),
            'shape': n.style('shape'),
            'opacity': parseFloat(n.style('opacity')),
            'label': n.style('label'),
            'color': n.style('color'),
            'font-size': parseFloat(n.style('font-size')),
            'overlay-color': n.style('overlay-color'),
            'overlay-opacity': parseFloat(n.style('overlay-opacity')) || 0,
          },
          classes: typeof n.classes === 'function' ? n.classes() : [],
        });
      });
    }

    const ext = cy.extent ? cy.extent() : null;
    return {
      available: true,
      zoom: typeof cy.zoom === 'function' ? cy.zoom() : undefined,
      pan: typeof cy.pan === 'function' ? cy.pan() : undefined,
      viewport: ext ? { width: Math.round(ext.w), height: Math.round(ext.h) } : undefined,
      counts: { nodes: allNodes.length, edges: allEdges.length, hidden: hiddenCount },
      palette_audit: paletteAudit,
      width_audit: widthAudit,
      nodes: includeElements ? nodeDumps : undefined,
      edges: includeElements ? edgeDumps : undefined,
    };
  } catch (e) {
    return { available: false, reason: `inspect_failed: ${String(e).slice(0, 200)}` };
  }
}

/** Returns true when `?canvasDebug=deep` is in the URL — gates the heavy
 *  Inspector-store + investigation-result blocks so the routine Ctrl+Shift+E
 *  snapshot stays small (~500KB) for sharing. Use deep mode when you need to
 *  audit "what's on canvas vs. what's in Node Inspector" — the audit script
 *  in `india-le-platform/scripts/audit_canvas_snapshot.mjs` requires it. */
export function isCanvasDebugDeep(): boolean {
  try {
    const v = new URLSearchParams(window.location.search).get('canvasDebug');
    return v === 'deep';
  } catch {
    return false;
  }
}

/** Snapshot of the per-node transaction store (what the Node Inspector
 *  Transfers/Counterparties tabs read). Returned as a plain object keyed by
 *  the lowercased node address — the same key the store uses internally. */
function _captureTransactionStore(): { available: boolean; reason?: string; node_count?: number; total_txs?: number; nodes?: Record<string, any> } {
  try {
    const store = (window as any).__useTransactionStore;
    if (!store || typeof store.getState !== 'function') {
      return { available: false, reason: 'tx_store_not_exposed' };
    }
    const map: Map<string, any[]> = store.getState().nodeTransactions;
    if (!map || typeof map.entries !== 'function') {
      return { available: false, reason: 'tx_store_empty' };
    }
    const out: Record<string, any> = {};
    let totalTxs = 0;
    for (const [addr, txs] of map.entries()) {
      const txList = Array.isArray(txs) ? txs : [];
      // Strip noisy fields, keep what an audit needs
      out[addr] = txList.map((t) => ({
        tx_hash: t.tx_hash || t.hash,
        from_address: t.from_address,
        to_address: t.to_address,
        value: t.value,
        token_symbol: t.token_symbol,
        timestamp: t.timestamp,
        direction: t.direction,
        counterparty: t.counterparty,
        counterparty_entity: t.counterparty_entity,
        counterparty_type: t.counterparty_type,
        block_height: t.block_height,
        is_trace: t.is_trace,
      }));
      totalTxs += txList.length;
    }
    return { available: true, node_count: Object.keys(out).length, total_txs: totalTxs, nodes: out };
  } catch (e) {
    return { available: false, reason: `tx_store_error: ${String(e).slice(0, 200)}` };
  }
}

/** Snapshot of the latest auto-investigation results (per-section status +
 *  data) and the active selection. Lets the audit script answer "what is the
 *  Inspector Analysis tab currently rendering" without re-running the
 *  investigation. */
function _captureInvestigation(): { available: boolean; reason?: string; root_id?: string | null; sections?: Record<string, any>; is_running?: boolean; is_complete?: boolean } {
  try {
    const store = (window as any).__useInvestigationStore;
    if (!store || typeof store.getState !== 'function') {
      return { available: false, reason: 'investigation_store_not_exposed' };
    }
    const state = store.getState();
    const inv = state.investigation;
    if (!inv) return { available: false, reason: 'no_investigation_synced' };
    const sectionsOut: Record<string, any> = {};
    const sections = inv.sections || {};
    for (const key of Object.keys(sections)) {
      const s = sections[key] || {};
      sectionsOut[key] = {
        status: s.status,
        error: s.error,
        // Keep `data` as-is — it's already the JSON the report renderer
        // consumes, so the audit script can replay narrative-vs-canvas
        // discrepancies (e.g. counterparty_triage says 12 cps, canvas
        // shows 8).
        data: s.data,
      };
    }
    return {
      available: true,
      root_id: state.investigationRootId,
      is_running: inv.isRunning,
      is_complete: inv.isComplete,
      sections: sectionsOut,
    };
  } catch (e) {
    return { available: false, reason: `investigation_error: ${String(e).slice(0, 200)}` };
  }
}

/** Snapshot of the active selection (selected node id, selected edge id) so
 *  the audit script knows which Node Inspector view was open at export
 *  time. */
function _captureSelection(): { available: boolean; reason?: string; selected_node_ids?: string[]; selected_edge_ids?: string[] } {
  try {
    const store = (window as any).__useSelectionStore;
    if (!store || typeof store.getState !== 'function') {
      return { available: false, reason: 'selection_store_not_exposed' };
    }
    const state = store.getState();
    return {
      available: true,
      selected_node_ids: Array.from(state.selectedNodeIds || []),
      selected_edge_ids: Array.from(state.selectedEdgeIds || []),
    };
  } catch (e) {
    return { available: false, reason: `selection_error: ${String(e).slice(0, 200)}` };
  }
}

/** Build the same payload `exportSession` writes to disk, but return it as
 *  a plain object. Used by the E2E harness so Playwright can capture the
 *  same JSON without going through a browser download. */
export function exportSessionAsObject(): any {
  let pipelineEntries: any[] = [];
  let pipelineSummary: any = {};
  try {
    const pr = (window as any).__pipelineRecorder;
    if (pr) {
      pipelineEntries = pr.getEntries();
      pipelineSummary = pr.getSummary();
    }
  } catch { /* pipeline recorder may not be loaded */ }

  const deep = isCanvasDebugDeep();

  // Round-trip through the safe replacer so circular refs are stripped
  // before the object leaves the browser context (Playwright serialises
  // the return value of page.evaluate via JSON.stringify anyway).
  const raw: Record<string, any> = {
    exported_at: new Date().toISOString(),
    user_agent: navigator.userAgent,
    url: window.location.href,
    deep_mode: deep,
    duration_ms: _sessionLog.length > 0 ? _sessionLog[_sessionLog.length - 1].ms : 0,
    entry_count: _sessionLog.length,
    entries: _sessionLog,
    pipeline: {
      entry_count: pipelineEntries.length,
      summary: pipelineSummary,
      entries: pipelineEntries,
      failures: pipelineEntries.filter((e: any) => !e.pass),
    },
    // Presentation-layer dump — computed styles + positions + sizes for
    // every element on the canvas. Lets a paste of this single JSON answer
    // both "what did the data layer decide" AND "what did the user see"
    // without needing a screenshot.
    canvas_state: inspectCanvasStyles(),
    selection: _captureSelection(),
  };
  if (deep) {
    raw.transaction_store = _captureTransactionStore();
    raw.investigation_results = _captureInvestigation();
  }
  try {
    return JSON.parse(JSON.stringify(raw, _safeJsonReplacer()));
  } catch (e) {
    return {
      exported_at: new Date().toISOString(),
      url: window.location.href,
      entry_count: _sessionLog.length,
      pipeline_entry_count: pipelineEntries.length,
      error: String(e),
    };
  }
}

/** Export the full session log as a downloadable JSON file. */
export function exportSession(): void {
  // Include pipeline recorder data alongside canvas debug data
  let pipelineEntries: any[] = [];
  let pipelineSummary: any = {};
  try {
    const pr = (window as any).__pipelineRecorder;
    if (pr) {
      pipelineEntries = pr.getEntries();
      pipelineSummary = pr.getSummary();
    }
  } catch { /* pipeline recorder may not be loaded */ }

  const deep = isCanvasDebugDeep();

  let serialised: string;
  try {
    const payload: Record<string, any> = {
      exported_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href,
      deep_mode: deep,
      duration_ms: _sessionLog.length > 0 ? _sessionLog[_sessionLog.length - 1].ms : 0,
      entry_count: _sessionLog.length,
      entries: _sessionLog,
      // Pipeline assertion data
      pipeline: {
        entry_count: pipelineEntries.length,
        summary: pipelineSummary,
        entries: pipelineEntries,
        failures: pipelineEntries.filter((e: any) => !e.pass),
      },
      // §10 presentation-layer audit — see inspectCanvasStyles() for what
      // this contains. Paired with the entries log above this is enough to
      // reproduce both what was decided and what was rendered.
      canvas_state: inspectCanvasStyles(),
      selection: _captureSelection(),
    };
    if (deep) {
      // `?canvasDebug=deep` — adds Inspector tx store + investigation
      // section results (the data the Inspector tabs render). The audit
      // script in india-le-platform/scripts/audit_canvas_snapshot.mjs
      // requires these blocks to cross-check canvas-vs-Inspector.
      payload.transaction_store = _captureTransactionStore();
      payload.investigation_results = _captureInvestigation();
    }
    serialised = JSON.stringify(payload, _safeJsonReplacer(), 2);
  } catch (e) {
    // Last-resort fallback — if even the safe replacer fails, dump
    // a minimal envelope so the user gets *something* and the error.
    console.error('[DEBUG] Export failed, writing minimal envelope:', e);
    serialised = JSON.stringify({
      exported_at: new Date().toISOString(),
      url: window.location.href,
      entry_count: _sessionLog.length,
      pipeline_entry_count: pipelineEntries.length,
      error: String(e),
      hint: 'JSON.stringify failed even with circular-safe replacer; check console.',
    }, null, 2);
  }

  const blob = new Blob([serialised], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const suffix = deep ? '-deep' : '';
  a.download = `debug-session${suffix}-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
  a.click();
  URL.revokeObjectURL(url);
  const failCount = pipelineEntries.filter((e: any) => !e.pass).length;
  const deepNote = deep ? ' [DEEP: tx_store + investigation_results included]' : '';
  console.log(
    `%c[DEBUG] Exported ${_sessionLog.length} canvas entries + ${pipelineEntries.length} pipeline entries (${failCount} failures)${deepNote}`,
    'background:#22c55e;color:#fff;padding:2px 8px;border-radius:4px'
  );
}

/** Get the raw session log (for programmatic access). */
export function getSessionLog(): DebugEntry[] {
  return _sessionLog;
}

/** Clear the session log. */
export function clearSessionLog(): void {
  _sessionLog.length = 0;
  _sessionStart = 0;
}

// Register Ctrl+Shift+E keyboard shortcut for canvas-session export.
// Was Ctrl+Shift+D but conflicted with Workbench's debug-mode toggle on the
// same combo — both fire on window, both prevent default, neither stops
// propagation, so the toast for debug-mode wins and the export silently
// no-ops.  Moved to E (for Export) to remove the conflict.
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && (e.key === 'E' || e.key === 'e')) {
      e.preventDefault();
      // Always export — even with an empty entries log, the canvas_state /
      // tx_store / investigation_results blocks are still useful for an
      // audit. Only warn if the user clearly hasn't enabled debug at all.
      if (!isCanvasDebugEnabled()) {
        console.log('%c[CANVAS DEBUG] Add ?canvasDebug=true (or =deep) to the URL to enable session recording. Exporting canvas snapshot anyway…', 'color:#f59e0b');
      } else if (_sessionLog.length === 0) {
        console.log('%c[CANVAS DEBUG] Session log is empty (no canvas mutations recorded). Exporting canvas + Inspector state only.', 'color:#f59e0b');
      }
      exportSession();
    }
  });
  // Expose the JSON-returning variant so the E2E harness can capture the
  // same payload Ctrl+Shift+E downloads, without triggering a file save.
  // Read-only, no security implication — only contains canvas events the
  // user produced themselves.
  (window as any).exportSessionAsObject = exportSessionAsObject;
  // Standalone style inspector — Playwright can call this directly when it
  // only needs the presentation-layer dump (faster than the full export).
  (window as any).inspectCanvasStyles = inspectCanvasStyles;
}

let _enabled: boolean | null = null;

export function isCanvasDebugEnabled(): boolean {
  if (_enabled === null) {
    try {
      // Accept any non-empty truthy value: 'true' (light), 'deep'
      // (Inspector + investigation blocks), or 'verbose' for any future
      // tier. Strict 'true' equality used to block 'deep' — Ctrl+Shift+E
      // would silently no-op because _sessionLog stayed empty.
      const v = new URLSearchParams(window.location.search).get('canvasDebug');
      _enabled = !!v && v !== 'false' && v !== '0' && v !== '';
      if (_enabled) {
        console.log(
          `%c[CANVAS DEBUG] Enabled (${v}) — logging all canvas element mutations`,
          'background:#1e40af;color:#fff;padding:2px 8px;border-radius:4px;font-weight:bold'
        );
      }
    } catch {
      _enabled = false;
    }
  }
  return _enabled;
}

type EdgeDecision = {
  path: string;             // which code path: 'expandNode' | 'addTransactionsToGraph' | 'handleAddCounterparty' | 'addTracePath' | 'sse'
  edgeId: string;
  source: string;
  target: string;
  direction: string;        // 'incoming' | 'outgoing' | 'inferred'
  fromAddress?: string;      // raw from_address from API
  toAddress?: string;        // raw to_address from API
  rootNodeId?: string;       // the node being expanded
  value?: number;
  tokenSymbol?: string;
  isConsolidated?: boolean;
  txCount?: number;
  edgeType?: string;         // 'gold-path' | 'synthetic-link' | 'dex-swap' | etc.
  reason?: string;           // free-text explanation of the decision
};

type NodeDecision = {
  path: string;
  nodeId: string;
  entityName?: string;
  entityType?: string;
  reason?: string;
};

type ConsolidationDecision = {
  key: string;               // e.g. "addr1->addr2"
  txCount: number;
  totalValue: number;
  direction: string;
  kept: boolean;
  reason?: string;
};

const _edgeLog: EdgeDecision[] = [];
const _nodeLog: NodeDecision[] = [];
const _consolidationLog: ConsolidationDecision[] = [];

export function logEdge(d: EdgeDecision): void {
  if (!isCanvasDebugEnabled()) return;
  _edgeLog.push(d);
  _record('EDGE', d.path, { src: d.source?.slice(0, 16), tgt: d.target?.slice(0, 16), dir: d.direction, val: d.value, token: d.tokenSymbol, type: d.edgeType });
  const arrow = d.direction === 'incoming' ? '←' : d.direction === 'outgoing' ? '→' : '↔';
  console.log(
    `%c[EDGE] %c${d.path}%c ${d.source.slice(0, 8)}… ${arrow} ${d.target.slice(0, 8)}… %c${d.direction}%c val=${d.value ?? '?'} ${d.tokenSymbol ?? ''} ${d.edgeType ? `type=${d.edgeType}` : ''} ${d.isConsolidated ? `(${d.txCount} txs consolidated)` : ''} ${d.reason ?? ''}`,
    'color:#f59e0b;font-weight:bold',
    'color:#60a5fa',
    'color:inherit',
    `color:${d.direction === 'incoming' ? '#22c55e' : d.direction === 'outgoing' ? '#ef4444' : '#9ca3af'}`,
    'color:inherit'
  );
  if (d.fromAddress && d.toAddress) {
    console.log(
      `       from_address=${d.fromAddress.slice(0, 12)}…  to_address=${d.toAddress.slice(0, 12)}…  root=${d.rootNodeId?.slice(0, 12) ?? '?'}…`
    );
  }
}

export function logNode(d: NodeDecision): void {
  if (!isCanvasDebugEnabled()) return;
  _nodeLog.push(d);
  _record('NODE', d.path, { id: d.nodeId?.slice(0, 16), entity: d.entityName, type: d.entityType });
  console.log(
    `%c[NODE] %c${d.path}%c ${d.nodeId.slice(0, 12)}… ${d.entityName ? `(${d.entityName})` : ''} ${d.entityType ? `[${d.entityType}]` : ''} ${d.reason ?? ''}`,
    'color:#22c55e;font-weight:bold',
    'color:#60a5fa',
    'color:inherit'
  );
}

export function logConsolidation(d: ConsolidationDecision): void {
  if (!isCanvasDebugEnabled()) return;
  _consolidationLog.push(d);
  const icon = d.kept ? '✓' : '✗';
  console.log(
    `%c[CONS] %c${icon} ${d.key}%c ${d.txCount} txs, val=${d.totalValue.toFixed(2)}, dir=${d.direction} ${d.reason ?? ''}`,
    'color:#a78bfa;font-weight:bold',
    `color:${d.kept ? '#22c55e' : '#ef4444'}`,
    'color:inherit'
  );
}

export function logLayoutEvent(event: string, data: Record<string, unknown>): void {
  if (!isCanvasDebugEnabled()) return;
  console.log(
    `%c[LAYOUT] %c${event}`,
    'color:#6366f1;font-weight:bold',
    'color:inherit',
    data
  );
}

// ── Pipeline debug helpers (data flow tracing) ──────────────────────

/** Log when elements are set/updated on the canvas store */
export function logCanvasState(event: string, elements: any[]): void {
  if (!isCanvasDebugEnabled()) return;
  const nodes = elements.filter((e: any) => !e.data?.source);
  const edges = elements.filter((e: any) => e.data?.source);
  const entities = nodes.filter((n: any) => n.data?.entity_name);
  const hidden = elements.filter((e: any) => (e.data?._visibility ?? 1) === 0);
  _record('CANVAS', event, { nodes: nodes.length, edges: edges.length, entities: entities.length, hidden: hidden.length, entityNames: entities.slice(0, 10).map((n: any) => n.data?.entity_name) });
  console.log(
    `%c[CANVAS] %c${event}%c  ${nodes.length} nodes (${entities.length} w/ entity), ${edges.length} edges, ${hidden.length} hidden`,
    'color:#3b82f6;font-weight:bold', 'color:#60a5fa', 'color:inherit'
  );
  if (entities.length > 0) {
    console.table(entities.slice(0, 20).map((n: any) => ({
      id: n.data.id?.slice(0, 16) + '…',
      entity: n.data.entity_name,
      type: n.data.entity_type || n.data.entity_category || '-',
      chain: n.data.chain || '-',
      visibility: n.data._visibility ?? 1,
    })));
  }
}

/** Log transaction store writes/reads */
export function logTxStore(event: string, address: string, txCount: number, sample?: any[]): void {
  if (!isCanvasDebugEnabled()) return;
  _record('TXSTORE', event, { address: address?.slice(0, 16), count: txCount, directions: sample?.slice(0, 5).map((t: any) => t.direction || '?'), sources: sample?.slice(0, 5).map((t: any) => t.data_source || '?') });
  console.log(
    `%c[TXSTORE] %c${event}%c  ${address.slice(0, 16)}…  ${txCount} txs`,
    'color:#f97316;font-weight:bold', 'color:#fb923c', 'color:inherit'
  );
  if (sample && sample.length > 0) {
    console.table(sample.slice(0, 10).map((tx: any) => ({
      hash: tx.tx_hash?.slice(0, 16) + '…',
      from: tx.from_address?.slice(0, 12) + '…',
      to: tx.to_address?.slice(0, 12) + '…',
      value: tx.value,
      token: tx.token_symbol || '-',
      direction: tx.direction || '?',
      source: tx.data_source || '?',
      date: tx.timestamp?.toString()?.slice(0, 19) || '?',
    })));
  }
}

/** Log triage data received */
export function logTriage(address: string, triage: any): void {
  if (!isCanvasDebugEnabled()) return;
  const s = triage?.summary || {};
  _record('TRIAGE', address?.slice(0, 16), { entity: s.entity_name, risk: s.risk_score, txTotal: triage?.transactions?.total, exchange: triage?.nearest_exchange?.name, exchangeHops: triage?.nearest_exchange?.hops, counterparties: triage?.counterparties?.length });
  console.log(
    `%c[TRIAGE] %c${address.slice(0, 16)}…%c  entity=%c${s.entity_name || 'none'}%c  risk=${s.risk_score ?? '?'}  txs=${triage?.transactions?.total ?? '?'}  exchange=${triage?.nearest_exchange?.name || 'none'}(${triage?.nearest_exchange?.hops ?? '?'} hops)`,
    'color:#8b5cf6;font-weight:bold', 'color:#a78bfa', 'color:inherit',
    `color:${s.entity_name ? '#22c55e' : '#9ca3af'};font-weight:bold`, 'color:inherit'
  );
  if (triage?.counterparties?.length > 0) {
    console.log(`  top counterparties: ${triage.counterparties.slice(0, 5).map((c: any) => `${c.address?.slice(0, 10)}…(${c.entity_name || '?'})`).join(', ')}`);
  }
}

/** Log entity enrichment results */
export function logEntityEnrich(event: string, count: number, entities: Record<string, any>): void {
  if (!isCanvasDebugEnabled()) return;
  const found = Object.entries(entities).filter(([, v]) => v?.name);
  _record('ENTITY', event, { checked: count, found: found.length, names: found.slice(0, 10).map(([, e]) => e.name) });
  console.log(
    `%c[ENTITY] %c${event}%c  ${count} addresses checked, ${found.length} entities found`,
    'color:#ec4899;font-weight:bold', 'color:#f472b6', 'color:inherit'
  );
  if (found.length > 0) {
    console.table(found.slice(0, 15).map(([addr, e]) => ({
      address: addr.slice(0, 16) + '…',
      name: e.name,
      category: e.category || '-',
      risk: e.risk_score ?? '-',
    })));
  }
}

/** Log Node Inspector selection and what data it receives */
export function logInspector(event: string, nodeId: string, data: Record<string, any>): void {
  if (!isCanvasDebugEnabled()) return;
  _record('INSPECTOR', event, { node: nodeId?.slice(0, 16), ...data });
  console.log(
    `%c[INSPECTOR] %c${event}%c  ${nodeId?.slice(0, 16)}…`,
    'color:#14b8a6;font-weight:bold', 'color:#2dd4bf', 'color:inherit',
    data
  );
}

/** Log auto-trace path data */
export function logTracePath(event: string, data: Record<string, any>): void {
  if (!isCanvasDebugEnabled()) return;
  _record('TRACE', event, data);
  console.log(
    `%c[TRACE] %c${event}`,
    'color:#eab308;font-weight:bold', 'color:#facc15',
    data
  );
}

/** Log Clear Canvas / New Investigation store reset */
export function logStoreReset(event: string, details: Record<string, any>): void {
  if (!isCanvasDebugEnabled()) return;
  _record('RESET', event, details);
  console.log(
    `%c[RESET] %c${event}`,
    'color:#ef4444;font-weight:bold', 'color:#f87171',
    details
  );
}

/** Dump accumulated decisions summary to console. Call after a batch operation completes. */
export function flushSummary(label: string): void {
  if (!isCanvasDebugEnabled()) return;
  if (_edgeLog.length === 0 && _nodeLog.length === 0) return;

  const incoming = _edgeLog.filter(e => e.direction === 'incoming').length;
  const outgoing = _edgeLog.filter(e => e.direction === 'outgoing').length;
  const other = _edgeLog.length - incoming - outgoing;

  console.groupCollapsed(
    `%c[CANVAS SUMMARY] ${label}: ${_nodeLog.length} nodes, ${_edgeLog.length} edges (${incoming} in / ${outgoing} out / ${other} other), ${_consolidationLog.length} consolidations`,
    'background:#1e40af;color:#fff;padding:2px 8px;border-radius:4px;font-weight:bold'
  );

  if (_edgeLog.length > 0) {
    console.table(_edgeLog.map(e => ({
      path: e.path,
      edge: `${e.source.slice(0, 8)}→${e.target.slice(0, 8)}`,
      dir: e.direction,
      val: e.value,
      token: e.tokenSymbol,
      type: e.edgeType || '-',
      cons: e.isConsolidated ? `${e.txCount}txs` : '-',
    })));
  }

  console.groupEnd();

  // Reset for next batch
  _edgeLog.length = 0;
  _nodeLog.length = 0;
  _consolidationLog.length = 0;
}
